/**
 * Appendix A.5 — Config registry. All tunables outside content tables.
 * Dev builds: hot_reload entries mutable at runtime. Release: deep-frozen at boot, writes throw.
 */

export interface ConfigEntry {
  path: string;
  type: 'number' | 'boolean' | 'string';
  default: number | boolean | string;
  min?: number;
  max?: number;
  hot_reload: boolean;
  owner: string;
}

export const REGISTRY: readonly ConfigEntry[] = [
  // --- movement & feel (§03) ---
  { path: 'player.move_speed', type: 'number', default: 8, min: 4, max: 16, hot_reload: true, owner: 'feel' },
  { path: 'player.accel', type: 'number', default: 64, min: 16, max: 256, hot_reload: true, owner: 'feel' },
  { path: 'player.brake', type: 'number', default: 96, min: 16, max: 384, hot_reload: true, owner: 'feel' },
  { path: 'player.reverse_decel', type: 'number', default: 128, min: 16, max: 512, hot_reload: true, owner: 'feel' },
  { path: 'player.max_hp', type: 'number', default: 100, min: 1, max: 1000, hot_reload: true, owner: 'feel' },
  { path: 'player.hitbox_radius', type: 'number', default: 0.7, min: 0.2, max: 3, hot_reload: true, owner: 'feel' },
  { path: 'player.mercy_s', type: 'number', default: 0.5, min: 0, max: 2, hot_reload: true, owner: 'feel' },
  { path: 'player.knockback_impulse', type: 'number', default: 4, min: 0, max: 20, hot_reload: true, owner: 'feel' },
  { path: 'player.knockback_decay', type: 'number', default: 40, min: 1, max: 200, hot_reload: true, owner: 'feel' },
  { path: 'player.pickup_radius', type: 'number', default: 3.2, min: 1, max: 20, hot_reload: true, owner: 'feel' },
  // dash (§03.4)
  { path: 'dash.speed', type: 'number', default: 32, min: 8, max: 80, hot_reload: true, owner: 'feel' },
  { path: 'dash.duration_s', type: 'number', default: 0.19, min: 0.05, max: 0.6, hot_reload: true, owner: 'feel' },
  { path: 'dash.iframes_s', type: 'number', default: 0.25, min: 0, max: 1, hot_reload: true, owner: 'feel' },
  { path: 'dash.cooldown_s', type: 'number', default: 2.0, min: 0.5, max: 10, hot_reload: true, owner: 'feel' },
  { path: 'dash.cooldown_floor_s', type: 'number', default: 1.0, min: 0.2, max: 5, hot_reload: true, owner: 'feel' },
  { path: 'dash.buffer_ms', type: 'number', default: 150, min: 0, max: 500, hot_reload: true, owner: 'feel' },
  // input (§03.5)
  { path: 'input.deadzone_inner', type: 'number', default: 0.18, min: 0.1, max: 0.3, hot_reload: true, owner: 'feel' },
  { path: 'input.deadzone_outer', type: 'number', default: 0.92, min: 0.8, max: 1.0, hot_reload: true, owner: 'feel' },
  { path: 'input.focus_engage', type: 'number', default: 0.4, min: 0.1, max: 0.9, hot_reload: true, owner: 'feel' },
  { path: 'input.focus_release', type: 'number', default: 0.3, min: 0.05, max: 0.9, hot_reload: true, owner: 'feel' },
  // Dash on touch is a double-tap (see src/input/touch.ts). This is the gap from the first tap's
  // release to the second touch-down, and is converted to sim ticks once at configure time — §3.6
  // windows count ticks. Distinct from `dash.buffer_ms`, which is how long an already-pressed dash
  // stays legal while the sim refuses it; one number for both would let dash forgiveness retune the
  // gesture. Floor 80 ms is five ticks, under which no player can close a pair reliably; ceiling
  // 600 ms is where two deliberate separate taps start fusing.
  { path: 'input.double_tap_ms', type: 'number', default: 200, min: 80, max: 600, hot_reload: true, owner: 'feel' },
  // targeting (§05)
  { path: 'targeting.hysteresis_margin', type: 'number', default: 0.15, min: 0, max: 1, hot_reload: true, owner: 'targeting' },
  { path: 'targeting.reacquire_ms', type: 'number', default: 100, min: 16, max: 500, hot_reload: true, owner: 'targeting' },
  { path: 'targeting.focus_cone_deg', type: 'number', default: 30, min: 5, max: 90, hot_reload: true, owner: 'targeting' },
  { path: 'targeting.focus_bonus', type: 'number', default: 0.35, min: 0, max: 2, hot_reload: true, owner: 'targeting' },
  { path: 'targeting.snap_rate_deg_s', type: 'number', default: 720, min: 90, max: 2000, hot_reload: true, owner: 'targeting' },
  { path: 'targeting.candidate_cap', type: 'number', default: 64, min: 8, max: 256, hot_reload: true, owner: 'targeting' },
  // arena (spine §6)
  { path: 'arena.width', type: 'number', default: 200, min: 60, max: 400, hot_reload: false, owner: 'arena' },
  { path: 'arena.height', type: 'number', default: 200, min: 60, max: 400, hot_reload: false, owner: 'arena' },
  { path: 'arena.spawn_safe_radius', type: 'number', default: 12, min: 4, max: 40, hot_reload: true, owner: 'director' },
  // director (§08)
  { path: 'director.budget_min0', type: 'number', default: 40, min: 5, max: 200, hot_reload: true, owner: 'director' },
  { path: 'director.budget_min19', type: 'number', default: 1200, min: 200, max: 4000, hot_reload: true, owner: 'director' },
  { path: 'director.recovery_dip', type: 'number', default: 0.3, min: 0, max: 0.9, hot_reload: true, owner: 'director' },
  { path: 'director.recovery_period_s', type: 'number', default: 90, min: 20, max: 300, hot_reload: true, owner: 'director' },
  { path: 'director.telegraph_min_s', type: 'number', default: 1.5, min: 0.2, max: 5, hot_reload: true, owner: 'director' },
  { path: 'director.run_duration_s', type: 'number', default: 1200, min: 60, max: 3600, hot_reload: false, owner: 'director' },
  // progression (§09)
  { path: 'progression.reroll_max', type: 'number', default: 3, min: 0, max: 10, hot_reload: true, owner: 'progression' },
  { path: 'progression.offer_count', type: 'number', default: 3, min: 2, max: 5, hot_reload: true, owner: 'progression' },
  { path: 'surge.max_tier', type: 'number', default: 10, min: 2, max: 20, hot_reload: true, owner: 'progression' },
  { path: 'surge.hit_penalty_tiers', type: 'number', default: 2, min: 0, max: 10, hot_reload: true, owner: 'progression' },
  // feedback (§03.10, §11)
  { path: 'feel.hitstop_cap_per_s', type: 'number', default: 8, min: 0, max: 30, hot_reload: true, owner: 'vfx' },
  { path: 'feel.hitstop_dilation', type: 'number', default: 0.1, min: 0.01, max: 1, hot_reload: true, owner: 'vfx' },
  { path: 'feel.shake_max_offset', type: 'number', default: 0.5, min: 0, max: 3, hot_reload: true, owner: 'vfx' },
  { path: 'feel.shake_decay', type: 'number', default: 1.2, min: 0.2, max: 5, hot_reload: true, owner: 'vfx' },
  { path: 'feel.shake_max_rot_deg', type: 'number', default: 0.5, min: 0, max: 5, hot_reload: true, owner: 'vfx' },
  // render (§11.1)
  { path: 'render.bloom_threshold', type: 'number', default: 0.32, min: 0, max: 1, hot_reload: true, owner: 'vfx' },
  { path: 'render.bloom_strength', type: 'number', default: 0.85, min: 0, max: 3, hot_reload: true, owner: 'vfx' },
  { path: 'render.bloom_radius', type: 'number', default: 0.5, min: 0, max: 2, hot_reload: true, owner: 'vfx' },
  { path: 'render.exposure', type: 'number', default: 1.15, min: 0.9, max: 1.4, hot_reload: true, owner: 'vfx' },
  { path: 'render.scanline_intensity', type: 'number', default: 0.18, min: 0, max: 0.6, hot_reload: true, owner: 'vfx' },
  { path: 'render.vhs_intensity', type: 'number', default: 0.22, min: 0, max: 1, hot_reload: true, owner: 'vfx' },
  // AMD-55 — the analogue-lens pair. `edge_blur` is in output pixels at the extreme corner
  // and the perf governor zeroes it below the desktop tier; `vignette` is corner darkening.
  { path: 'render.edge_blur', type: 'number', default: 2.2, min: 0, max: 6, hot_reload: true, owner: 'vfx' },
  { path: 'render.vignette', type: 'number', default: 0.28, min: 0, max: 0.6, hot_reload: true, owner: 'vfx' },
  { path: 'render.pixel_scale', type: 'number', default: 3, min: 1, max: 6, hot_reload: true, owner: 'vfx' },
  { path: 'render.density_alpha_floor', type: 'number', default: 0.75, min: 0.4, max: 1, hot_reload: true, owner: 'vfx' },
  // AMD-8 threat radar. `render.radar` also gates the off-screen threat arrows: they
  // are one signal in two forms and turning half of it off would be worse than either.
  { path: 'render.radar', type: 'boolean', default: true, hot_reload: true, owner: 'vfx' },
  // R6 weapon-range ring. Draws `targeting.visible_range_u` as a circle on the arena
  // floor around the ship, so the acquisition clamp is a thing you can see rather than
  // a number you have to infer from which enemies your guns ignore. Off is a real off:
  // the mesh is hidden and its per-frame rebuild is skipped entirely.
  { path: 'render.range_ring', type: 'boolean', default: true, hot_reload: true, owner: 'vfx' },
  // AMD-24 curved horizon. The world's camera-relative offsets are divided by 1 + c·r²
  // with r in units of the view half-diagonal, so the arena reads as the top of a sphere
  // rather than a plane. 0 is a real off switch — the vertex programs take the flat
  // branch and the projection is identical to the pre-AMD-24 one. Scaled down on small
  // viewports (see `curvatureForViewport`), following AMD-6 D12.
  { path: 'render.curvature', type: 'number', default: 0.16, min: 0, max: 0.5, hot_reload: true, owner: 'vfx' },
  // §10.7.4 storm veil depth — the PEAK multiplier the veil reaches at full intensity,
  // outside the protected §5.8 disc. The storm itself is a sim event and this key cannot
  // stop it: bolts still fall, thunder still cracks and the radar still takes its
  // interference at 0. What 0 removes is only the picture of the weather, which is the
  // right shape for a comfort setting — it can never hand one player information that
  // another player is denied.
  { path: 'render.storm_veil', type: 'number', default: 0.72, min: 0, max: 0.85, hot_reload: true, owner: 'vfx' },
  // §11.16 gas clouds. Pure decoration at `LAYER_GAS`, below every batch that carries a
  // meaning, so off is a real off and costs no information at all.
  { path: 'render.gas_clouds', type: 'boolean', default: true, hot_reload: true, owner: 'vfx' },
  // budgets (§14)
  { path: 'budget.max_enemies', type: 'number', default: 500, min: 20, max: 500, hot_reload: false, owner: 'perf' },
  { path: 'budget.max_projectiles', type: 'number', default: 1500, min: 50, max: 1500, hot_reload: false, owner: 'perf' },
  { path: 'budget.max_particles', type: 'number', default: 3000, min: 100, max: 3000, hot_reload: false, owner: 'perf' },
  { path: 'perf.gpu_up_threshold_ms', type: 'number', default: 7.0, min: 2, max: 16, hot_reload: true, owner: 'perf' },
  { path: 'perf.gpu_down_threshold_ms', type: 'number', default: 4.5, min: 1, max: 16, hot_reload: true, owner: 'perf' },
  { path: 'perf.up_frames', type: 'number', default: 30, min: 5, max: 300, hot_reload: true, owner: 'perf' },
  { path: 'perf.down_frames', type: 'number', default: 600, min: 30, max: 3000, hot_reload: true, owner: 'perf' },
  { path: 'perf.step_cooldown_frames', type: 'number', default: 120, min: 10, max: 600, hot_reload: true, owner: 'perf' },
  { path: 'perf.auto_balance', type: 'boolean', default: true, hot_reload: true, owner: 'perf' },
  // dev
  { path: 'dev.overlay', type: 'boolean', default: false, hot_reload: true, owner: 'perf' },
  // pointer-follow movement (§03.2 wish vector, §03.5 remap) — both in CSS px, see src/input/follow.ts
  { path: 'input.follow_full_speed_px', type: 'number', default: 120, min: 40, max: 400, hot_reload: true, owner: 'feel' },
  { path: 'input.follow_stop_radius_px', type: 'number', default: 28, min: 4, max: 120, hot_reload: true, owner: 'feel' },
  { path: 'bench.god_mode', type: 'boolean', default: false, hot_reload: true, owner: 'perf' },
  // §5.8 on-screen acquisition clamp. No weapon acquires past this radius from the ship,
  // whatever its own `range_u` says, so shots never leave toward a body off the frame.
  // 30 u is the half-diagonal of the reference 53.3 × 30 u framing (src/render/camera.ts,
  // BASE_VIEW_HEIGHT), so everything inside the reference frame stays targetable. R6 draws
  // the ring from this entry — one number, one radius, no second source of truth.
  { path: 'targeting.visible_range_u', type: 'number', default: 18, min: 8, max: 60, hot_reload: true, owner: 'targeting' },
  // AMD-35 §9.12 the Graft. One switch and nothing else: every number the in-run gacha
  // uses (income, cost curve, rarity weights, pity threshold, pool weights) lives in
  // `src/content/gacha.json`, which is hashed into `content_hash` like any other table.
  // Two sources of truth for a rarity weight is how a rarity weight ends up wrong.
  { path: 'gacha.enabled', type: 'boolean', default: true, hot_reload: true, owner: 'progression' },
  // AMD-39 §9.13 the Hold meter. Speeds are stated against the §3.2 top speed of 8 u/s:
  // 0.8 u/s is 10% (one tick of the 64 u/s² ramp already exceeds it, so a nudge costs
  // something) and 2.4 u/s is 30%. Break-even sits at 1.2 u/s. `threat_full` is the summed
  // `threat_score` inside the §5.8 ring at which the payout saturates — 120 is roughly a
  // dozen mid-tier bodies, two elites, or a boss plus an escort.
  { path: 'hold.fill_s', type: 'number', default: 2.0, min: 0.25, max: 10, hot_reload: true, owner: 'progression' },
  { path: 'hold.drain_mult', type: 'number', default: 3, min: 1, max: 10, hot_reload: true, owner: 'progression' },
  { path: 'hold.still_speed_u', type: 'number', default: 0.8, min: 0, max: 4, hot_reload: true, owner: 'progression' },
  { path: 'hold.move_speed_u', type: 'number', default: 2.4, min: 0.1, max: 8, hot_reload: true, owner: 'progression' },
  // AMD-40 recalibrated these onto the closing measure, whose scale is a quarter of the
  // presence measure's: 12 was read as roughly three committed driftfin or one gloomsquid.
  //
  // AMD-46 recalibrated them again, downward, because 12 is a density that only exists after
  // minute 8. Measured over three seeds, a §9.13 stander's mean arriving weight is 1.85 —
  // the director spends 40–160 threat in the minutes a stander has to survive, spread over a
  // 200 u torus, and an anchored ship clears what walks in. So the card's numbers, all of
  // which are quoted "at full threat", were paying out at 0.19 for the entire window in which
  // the protocol lives or dies: the payout gate was calibrated to a density you can only
  // reach by first surviving without it. The saturating curve is unchanged and so is the top
  // of it — anything past ~6 arriving weight already saturated under both settings — this
  // only lifts the thin end, which is the only end a stander ever sees.
  { path: 'hold.threat_full', type: 'number', default: 4, min: 1, max: 4000, hot_reload: true, owner: 'progression' },
  { path: 'hold.threat_half', type: 'number', default: 1.5, min: 0.1, max: 1000000, hot_reload: true, owner: 'progression' },
  // The deadband still returns exactly zero for an empty ring — the property the whole design
  // rests on — and mode 3 already scores a body merely drifting past at ~0, so the belt-and-
  // braces half of this can afford to be smaller than one minor body's weight.
  { path: 'hold.threat_floor', type: 'number', default: 0.5, min: 0, max: 200, hot_reload: true, owner: 'progression' },
  { path: 'hold.boss_threat', type: 'number', default: 80, min: 0, max: 500, hot_reload: true, owner: 'progression' },
  { path: 'hold.minor_threat', type: 'number', default: 2, min: 0, max: 50, hot_reload: true, owner: 'progression' },
  // AMD-40 §9.13.3 — threat is what is ARRIVING, not what is present. 0 presence (the
  // AMD-39 measure), 1 closing rate, 2 contact band, 3 max(band, closing). A mover's ring
  // is full of a receding tail; a stander's is full of bodies walking in, and only mode 3
  // tells those apart in both directions. `close_ref_u` = 4 saturates on any archetype
  // committing at half its own speed; `contact_u` / `band_u` bracket the hull.
  { path: 'hold.threat_mode', type: 'number', default: 3, min: 0, max: 3, hot_reload: true, owner: 'progression' },
  // AMD-46 — the outer edge of "committed to you". Measured on a planted ship: 0.8 bodies on
  // the hull, 0.05 between 4 and 18 u, and 9.5 beyond 30 u walking in. An anchored pilot's
  // weapons clear the 18 u acquisition ring faster than the director refills it, so a measure
  // that starts at 18 u sees an empty ring for two thirds of every run and pays nothing for
  // the two thirds — while the thing that actually kills the ship is the column crossing 40 u
  // right now. Weight fades linearly from full at `targeting.visible_range_u` to nothing here,
  // so a body only counts for as much of the ring as it has actually crossed.
  { path: 'hold.hunt_ring_u', type: 'number', default: 44, min: 8, max: 100, hot_reload: true, owner: 'progression' },
  { path: 'hold.close_ref_u', type: 'number', default: 4, min: 0.5, max: 32, hot_reload: true, owner: 'progression' },
  { path: 'hold.contact_u', type: 'number', default: 4, min: 0.5, max: 18, hot_reload: true, owner: 'progression' },
  { path: 'hold.band_u', type: 'number', default: 9, min: 1, max: 18, hot_reload: true, owner: 'progression' },
];

type Value = number | boolean | string;

class ConfigRegistry {
  private values = new Map<string, Value>();
  private entries = new Map<string, ConfigEntry>();
  private frozen = false;

  constructor() {
    for (const e of REGISTRY) {
      this.entries.set(e.path, e);
      this.values.set(e.path, e.default);
    }
  }

  get(path: string): number {
    const v = this.values.get(path);
    if (v === undefined) throw new Error(`config: unknown path "${path}"`);
    return v as number;
  }

  getBool(path: string): boolean {
    const v = this.values.get(path);
    if (v === undefined) throw new Error(`config: unknown path "${path}"`);
    return v as boolean;
  }

  set(path: string, value: Value): void {
    if (this.frozen) throw new Error(`config: registry frozen, cannot write "${path}"`);
    const e = this.entries.get(path);
    if (!e) throw new Error(`config: unknown path "${path}"`);
    if (typeof value === 'number' && e.type === 'number') {
      if (e.min !== undefined && value < e.min) value = e.min;
      if (e.max !== undefined && value > e.max) value = e.max;
    }
    this.values.set(path, value);
  }

  /** Release builds call this at boot (A.5). */
  freeze(): void {
    this.frozen = true;
  }

  get isFrozen(): boolean {
    return this.frozen;
  }

  /** Deterministic snapshot for content_hash (A.6). */
  snapshot(): Record<string, Value> {
    const out: Record<string, Value> = {};
    for (const path of [...this.values.keys()].sort()) out[path] = this.values.get(path)!;
    return out;
  }

  resetDefaults(): void {
    for (const e of REGISTRY) this.values.set(e.path, e.default);
  }
}

export const cfg = new ConfigRegistry();
